import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import { Bar } from "react-chartjs-2";
import { AXIS_LABEL_STYLE, defaultOptions, defaultPlugins, defaultRootPlugins } from "./config";

const X_BAR_THICKNESS = 40;
const Y_BAR_THICKNESS = 20;
const ACTIVE_GRID_STYLE = { color: "#292E3C", lineWidth: 1.8, tickWidth: 0 };
const INACTIVE_GRID_STYLE = { display: false };

const StackChart = ({ axis = "y", data, labels, title, parser, onClick }) => {
  const chartRef = useRef(null);
  useEffect(() => {
    chartRef.current.tooltip.total = true;
    chartRef.current.tooltip.row = true;
    chartRef.current.onClick = onClick;
  });

  const parsing = { xAxisKey: parser.x, yAxisKey: parser.y };

  const formattedData = useMemo(() => {
    const datasets = [];

    data.forEach(d => {
      const set = { label: d.model, data: d[parser.y], barThickness: axis === "y" ? Y_BAR_THICKNESS : X_BAR_THICKNESS };
      if (d.color) {
        set.backgroundColor = d.color;
        set.hoverBackgroundColor = d.color;
      }

      datasets.push(set);
    });

    return { labels, datasets };
  }, [ axis, data, labels, parser ]);

  const titleOptions = {
    x: { display: true, text: parser.xLabel, ...AXIS_LABEL_STYLE },
    y: { display: true, text: parser.yLabel, ...AXIS_LABEL_STYLE },
  };

  return (
    <Bar
      data={formattedData}
      options={{
        ...defaultOptions,
        ...parsing,

        scales: {
          x: {
            grid: axis === "y" ? ACTIVE_GRID_STYLE : INACTIVE_GRID_STYLE,
            border: { display: false },
            stacked: true,
            title: titleOptions.x,
            ticks: { precision: 0 },
            suggestedMax: 5,
          },
          y: {
            grid: axis === "x" ? ACTIVE_GRID_STYLE : INACTIVE_GRID_STYLE,
            border: { display: false },
            stacked: true,
            title: titleOptions.y,
            ticks: { precision: 0 },
            suggestedMax: 5,
          }
        },

        plugins: {
          legend: { ...defaultPlugins.legend },
          tooltip: { ...defaultPlugins.tooltip, intersect: false, mode: axis },
          title: title,
        },
        indexAxis: axis,
      }}
      plugins={defaultRootPlugins}
      ref={chartRef}
    />
  );
};

StackChart.propTypes = {
  axis: PropTypes.oneOf([ "x", "y" ]),
  labels: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.array,
  title: PropTypes.string,
  parser: PropTypes.shape({
    x: PropTypes.string,
    y: PropTypes.string,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
  }),
  onClick: PropTypes.func,
};

export default StackChart;
